import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';
import { TESTIMONIALS } from '../../data/testimonials.data';
import { TestimonialCardComponent } from './testimonial-card.component';
import { ScrollAnimationDirective } from '../../directives/scroll-animation.directive';
import { slideAnimation } from '../../animations/slide.animation';

@Component({
  selector: 'app-testimonials',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule, TestimonialCardComponent, ScrollAnimationDirective, TranslateModule],
  animations: [slideAnimation],
  template: `
    <section id="testimonials" class="section testimonials-section">
      <div class="container">
        <h2 class="section-title text-center main-heading" scrollAnimation>{{'TESTIMONIALS.TITLE' | translate}}</h2>
        <p class="section-subtitle text-center" scrollAnimation>
          {{'TESTIMONIALS.SUBTITLE' | translate}}
        </p>
        <div class="testimonial-container">
          <button mat-icon-button class="nav-button prev" (click)="previous()">
            <mat-icon>chevron_left</mat-icon>
          </button>
          
          <div class="testimonial-viewport">
            <div class="testimonial-track" 
                [@slideAnimation]="{
                  value: currentIndex,
                  params: {
                    from: 'translateX(' + currentPosition + '%)',
                    to: 'translateX(' + targetPosition + '%)'
                  }
                }">
              <app-testimonial-card 
                [testimonial]="testimonials[currentIndex]">
              </app-testimonial-card>
            </div>
          </div>

          <button mat-icon-button class="nav-button next" (click)="next()">
            <mat-icon>chevron_right</mat-icon>
          </button>
        </div>

        <div class="progress-bar">
          <div class="progress-dots">
            <span 
              *ngFor="let _ of testimonials; let i = index" 
              class="dot"
              [class.active]="i === currentIndex"
              (click)="goToSlide(i)">
            </span>
          </div>
        </div>
      </div>
    </section>
  `,
  styles: [`
    .testimonials-section {
      background: var(--background-color);
      padding: 4rem 0;
    }

    .main-heading {
      font-family: var(--heading-font-family, 'Poppins', sans-serif);
      font-size: 2.5rem;
      font-weight: 700;
      color: #300458;
      margin-bottom: 1.5rem;
      letter-spacing: 0.5px;
      text-transform: uppercase;
    }

    .section-subtitle {
      font-family: var(--body-font-family, 'Roboto', sans-serif);
      font-size: 1.1rem;
      color: #666;
      max-width: 700px;
      margin: 0 auto 2.5rem;
      line-height: 1.6;
    }

    .testimonial-container {
      max-width: 900px;
      margin: 0 auto;
      position: relative;
      padding: 0 40px;
    }

    .testimonial-viewport {
      overflow: hidden;
      padding: 2rem 0;
    }

    .testimonial-track {
      display: flex;
      transition: transform 0.5s ease-out;
    }

    .nav-button {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      background-color: white !important;
      box-shadow: var(--box-shadow);
      z-index: 10;
    }

    .nav-button.prev {
      left: 0;
    }

    .nav-button.next {
      right: 0;
    }

    .progress-bar {
      margin-top: 2rem;
      text-align: center;
    }

    .progress-dots {
      display: flex;
      justify-content: center;
      gap: 8px;
    }

    .dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background-color: #ddd;
      cursor: pointer;
      transition: all 0.3s ease;
    }

    .dot.active {
      background-color: var(--primary-color);
      transform: scale(1.2);
    }

    @media (max-width: 768px) {
      .testimonial-container {
        padding: 0 30px;
      }
    }
  `]
})
export class TestimonialsComponent implements OnInit, OnDestroy {
  testimonials = TESTIMONIALS;
  currentIndex = 0;
  currentPosition = 0;
  targetPosition = 0;
  private interval: any;

  ngOnInit() {
    this.startAutoSlide();
  }

  ngOnDestroy() {
    if (this.interval) {
      clearInterval(this.interval);
    }
  }

  private startAutoSlide() {
    this.interval = setInterval(() => {
      this.next();
    }, 5000);
  }

  previous() {
    this.currentPosition = this.targetPosition;
    this.currentIndex = this.currentIndex > 0 ? this.currentIndex - 1 : this.testimonials.length - 1;
    this.targetPosition = -100 * this.currentIndex;
  }

  next() {
    this.currentPosition = this.targetPosition;
    this.currentIndex = this.currentIndex < this.testimonials.length - 1 ? this.currentIndex + 1 : 0;
    this.targetPosition = -100 * this.currentIndex;
  }

  goToSlide(index: number) {
    this.currentPosition = this.targetPosition;
    this.currentIndex = index;
    this.targetPosition = -100 * this.currentIndex;
  }
}